import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  FunnelChart,
  Funnel,
  LabelList
} from 'recharts';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Chip,
  Sheet,
  Divider,
  Alert,
  CircularProgress,
  Button,
  Select,
  Option,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  Tooltip as JoyTooltip
} from '@mui/joy';
import { FaChartLine, FaChartBar, FaChartPie, FaChartArea, FaTachometerAlt, FaCalendarAlt, FaDollarSign, FaUsers, FaBoxes, FaClipboardList, FaCheckCircle, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { directSupabase } from '../lib/supabaseClient';
import { isDoorType } from '../modules/docs/utils/visualDesignerSummary';
import { Project } from '../types/project';
import { loadWorkspaceSnapshots } from './project-workspace/workspaceDashboard.service';
import { workspaceLifecycleStatus } from '../utils/workflowUtils';
import { WORKSPACE_STAGE_LIST, type WorkspaceStage } from './project-workspace/workspaceStages';
import { IconWrapper } from '../components/IconWrapper';
import { Link, useNavigate } from 'react-router-dom';
import {
  buildAdditionalCostsDrilldownPath,
  buildDashboardRangeQuery,
  buildDocsInvoiceFilterPath,
  buildDocsQuoteEstimateAwaitingFilterPath,
  buildDocsQuoteEstimateFilterPath,
  buildDocsQuoteEstimateStatusFilterPath,
  buildDocsVatFilterPath,
  buildFenestrationDrilldownPath,
  buildProjectsFilterPath,
} from '../utils/dashboardDrilldowns';

// Workflow stage colors
// Same stages as the Workspaces Board, worked out from each job's records
const stageColors: Record<WorkspaceStage, string> = {
  presale: '#ffc658',
  deposit: '#8884d8',
  ordering: '#00C49F',
  installation: '#ff7300',
  aftercare: '#82ca9d',
  completed: '#0088FE',
  cancelled: '#9aa5b1',
};

type InvoiceStatusView = 'paid' | 'sent';
type CategoryBreakdown = Record<string, number>;
type DashboardTimeRange =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'this_financial_year'
  | 'last_financial_year'
  | 'ytd';

const DASHBOARD_TIME_RANGE_SESSION_KEY = 'dashboard_time_range';
const DASHBOARD_TIME_RANGE_OPTIONS: DashboardTimeRange[] = [
  'this_month',
  'last_month',
  'this_quarter',
  'last_quarter',
  'this_year',
  'last_year',
  'this_financial_year',
  'last_financial_year',
  'ytd'
];

const isDashboardTimeRange = (value: string | null | undefined): value is DashboardTimeRange => {
  if (!value) return false;
  return DASHBOARD_TIME_RANGE_OPTIONS.includes(value as DashboardTimeRange);
};

const InvoiceStatusScoped: React.FC<{
  defaultValue?: InvoiceStatusView;
  children: (args: { statusView: InvoiceStatusView; setStatusView: (value: InvoiceStatusView) => void }) => React.ReactNode;
}> = ({ defaultValue = 'paid', children }) => {
  const [statusView, setStatusView] = useState<InvoiceStatusView>(defaultValue);
  return <>{children({ statusView, setStatusView })}</>;
};

const DashboardPage: React.FC = () => {
  const [timeRange, setTimeRange] = useState<DashboardTimeRange>(() => {
    try {
      const stored = sessionStorage.getItem(DASHBOARD_TIME_RANGE_SESSION_KEY);
      return isDashboardTimeRange(stored) ? stored : 'this_month';
    } catch {
      return 'this_month';
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectStats, setProjectStats] = useState<any>({});
  const [invoiceTotals, setInvoiceTotals] = useState<{
    grandTotal: number;
    paidTotal: number;
    sentTotal: number;
    unpaidTotal: number;
    totalQuotes: number;
    paidQuotes: number;
    unpaidQuotes: number;
    totalInvoices: number;
    paidInvoices: number;
    sentInvoices: number;
    unpaidInvoices: number;
    pipelineTotalQuotesEstimates: number;
    pipelineNonDraftQuotesEstimates: number;
    pipelineAcceptedQuotesEstimates: number;
    pipelineRejectedQuotesEstimates: number;
    pipelineDraftSentQuotesEstimates: number;
    pipelineCashQuotesEstimates: number;
    pipelineAcceptedRejectedQuotesEstimates: number;
    pipelineConversionRate: number;
  }>({
    grandTotal: 0,
    paidTotal: 0,
    sentTotal: 0,
    unpaidTotal: 0,
    totalQuotes: 0,
    paidQuotes: 0,
    unpaidQuotes: 0,
    totalInvoices: 0,
    paidInvoices: 0,
    sentInvoices: 0,
    unpaidInvoices: 0,
    pipelineTotalQuotesEstimates: 0,
    pipelineNonDraftQuotesEstimates: 0,
    pipelineAcceptedQuotesEstimates: 0,
    pipelineRejectedQuotesEstimates: 0,
    pipelineDraftSentQuotesEstimates: 0,
    pipelineCashQuotesEstimates: 0,
    pipelineAcceptedRejectedQuotesEstimates: 0,
    pipelineConversionRate: 0
  });
  const [vatSummary, setVatSummary] = useState<{
    inputVat: number;
    outputVat: number;
    netVatPosition: number;
  }>({
    inputVat: 0,
    outputVat: 0,
    netVatPosition: 0
  });
  const [vatWorkingsOpen, setVatWorkingsOpen] = useState(false);
  const [vatWorkingsRows, setVatWorkingsRows] = useState<Array<{
    documentId: string;
    ref: string;
    customerName: string;
    inputVat: number;
    outputVat: number;
    netOff: number;
  }>>([]);
  const [fenestrationSummary, setFenestrationSummary] = useState<{
    internalFinishCounts: Record<string, number>;
    externalFinishCounts: Record<string, number>;
    materialTypeCounts: Record<string, number>;
    typeCounts: Record<string, number>;
    glassTypeCounts: Record<string, number>;
    sameDimensionOccurrences: number;
    withinFiftyMmDimensionOccurrences: number;
    meanWindowWidth: number;
    meanWindowHeight: number;
    meanDoorWidth: number;
    meanDoorHeight: number;
    totalOpeners: number;
    itemsWithOpeners: number;
    totalFenestrationItems: number;
  }>({
    internalFinishCounts: {},
    externalFinishCounts: {},
    materialTypeCounts: {},
    typeCounts: {},
    glassTypeCounts: {},
    sameDimensionOccurrences: 0,
    withinFiftyMmDimensionOccurrences: 0,
    meanWindowWidth: 0,
    meanWindowHeight: 0,
    meanDoorWidth: 0,
    meanDoorHeight: 0,
    totalOpeners: 0,
    itemsWithOpeners: 0,
    totalFenestrationItems: 0
  });
  const [additionalCostTypeTotals, setAdditionalCostTypeTotals] = useState<Record<string, number>>({});
  const [acceptedAdditionalCostTypeTotals, setAcceptedAdditionalCostTypeTotals] = useState<Record<string, number>>({});

  const [revenueByCategoryByStatus, setRevenueByCategoryByStatus] = useState<Record<InvoiceStatusView, CategoryBreakdown>>({
    paid: {},
    sent: {}
  });
  const [categoryLabelsByKey, setCategoryLabelsByKey] = useState<Record<string, string>>({});
  const [revenueByPostcodeByStatus, setRevenueByPostcodeByStatus] = useState<Record<InvoiceStatusView, Record<string, number>>>({
    paid: {},
    sent: {}
  });
  const [projectsByPostcodeByStatus, setProjectsByPostcodeByStatus] = useState<Record<InvoiceStatusView, Record<string, { inProgress: number; complete: number }>>>({
    paid: {},
    sent: {}
  });
  const navigate = useNavigate();

  const getDateRangeForTimeRange = (range: DashboardTimeRange): { from: Date; to: Date; label: string } => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
    const quarterStart = new Date(now.getFullYear(), quarterStartMonth, 1);
    const nextQuarterStart = new Date(now.getFullYear(), quarterStartMonth + 3, 1);
    const currentFyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1; // FY starts 1 Apr
    const currentFyStart = new Date(currentFyStartYear, 3, 1);
    const nextFyStart = new Date(currentFyStartYear + 1, 3, 1);
    const lastFyStart = new Date(currentFyStartYear - 1, 3, 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);

    switch (range) {
      case 'this_month':
        return { from: monthStart, to: now, label: 'This Month' };
      case 'last_month':
        return {
          from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          to: monthStart,
          label: 'Last Month'
        };
      case 'this_quarter':
        return { from: quarterStart, to: now, label: 'This Quarter' };
      case 'last_quarter':
        return {
          from: new Date(now.getFullYear(), quarterStartMonth - 3, 1),
          to: quarterStart,
          label: 'Last Quarter'
        };
      case 'this_year':
        return { from: yearStart, to: now, label: 'This Year' };
      case 'last_year':
        return { from: lastYearStart, to: yearStart, label: 'Last Year' };
      case 'this_financial_year':
        return { from: currentFyStart, to: now, label: 'This Financial Year' };
      case 'last_financial_year':
        return { from: lastFyStart, to: currentFyStart, label: 'Last Financial Year' };
      case 'ytd':
        return { from: yearStart, to: now, label: 'Year to Date (YTD)' };
      default:
        return { from: todayStart, to: now, label: 'This Month' };
    }
  };

  const getProjectCategoryEntries = (project: any): Array<{ key: string; label: string }> => {
    const productTypes = Array.isArray(project?.product_types) ? project.product_types : [];
    const uniqueByKey = new Map<string, string>();

    productTypes.forEach((type: string) => {
      const label = String(type || '').trim();
      if (!label) return;
      const key = label.toLowerCase();
      if (!uniqueByKey.has(key)) {
        uniqueByKey.set(key, label);
      }
    });

    if (uniqueByKey.size === 0) {
      return [{ key: 'undisclosed', label: 'Undisclosed' }];
    }

    return Array.from(uniqueByKey.entries()).map(([key, label]) => ({ key, label }));
  };

  const extractPostcodeFromAddress = (address?: string | null): string => {
    const fullAddress = String(address || '').trim();
    if (!fullAddress) return '';
    // Scan the full address text and use the last UK postcode-shaped token.
    const matches = fullAddress.match(/([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})/gi);
    if (!matches || matches.length === 0) return '';
    return String(matches[matches.length - 1] || '').toUpperCase().replace(/\s+/g, ' ').trim();
  };

  const getLifecycleTimestamp = (value: string | null | undefined): number | null => {
    if (!value) return null;
    const ts = new Date(value).getTime();
    return Number.isFinite(ts) ? ts : null;
  };

  // Fetch invoice totals and dashboard revenue breakdowns from JARVIS documents
  const fetchInvoiceTotals = async () => {
    try {
      const range = getDateRangeForTimeRange(timeRange);

      const { data: docs, error: docsError } = await directSupabase
        .from('documents')
        .select('id, kind, title, status, gross_total, vat_amount, vat_pct, input_vat_selected, customer_first_name, customer_last_name, project_id, customer_id, lifecycle_ref, organisation_id, active_version_id, created_at, draft_at, sent_at, accepted_at, rejected_at, invoiced_at, paid_at, voided_at')
        .neq('status', 'void');

      if (docsError) {
        throw new Error(docsError.message || 'Failed to fetch documents');
      }

      const rows = Array.isArray(docs) ? docs : [];
      const rangeFromTs = range.from.getTime();
      const rangeToTs = range.to.getTime();
      const toTs = (value: any): number | null => {
        if (!value) return null;
        const ts = new Date(value).getTime();
        return Number.isFinite(ts) ? ts : null;
      };
      const inRangeTs = (value: any): boolean => {
        const ts = toTs(value);
        return ts != null && ts >= rangeFromTs && ts < rangeToTs;
      };
      const getStatusEventTimestamp = (row: any): any => {
        const status = String(row.status || '').toLowerCase();
        if (status === 'paid') return row.paid_at;
        if (status === 'cash') return row.paid_at;
        if (status === 'sent') return row.sent_at;
        if (status === 'draft') return row.draft_at;
        if (status === 'accepted') return row.accepted_at;
        if (status === 'rejected') return row.rejected_at;
        if (status === 'invoiced') return row.invoiced_at;
        if (status === 'void') return row.voided_at;
        return row.created_at;
      };
      const invoices = rows.filter((row: any) => row.kind === 'Invoice');
      const quoteEstimateRows = rows.filter((row: any) => row.kind === 'Quotation' || row.kind === 'Estimate');
      const quoteEstimateByLifecycle = new Map<string, any>();
      quoteEstimateRows.forEach((row: any) => {
        const lifecycleRef = String(row.lifecycle_ref || '').trim();
        const orgId = String(row.organisation_id || '');
        const key = lifecycleRef ? `${orgId}:${lifecycleRef}` : `doc:${row.id}`;
        // Keep latest quote/estimate per lifecycle to represent the active document.
        const existing = quoteEstimateByLifecycle.get(key);
        if (!existing) {
          quoteEstimateByLifecycle.set(key, row);
          return;
        }
        const existingTs = new Date(existing.created_at || 0).getTime();
        const currentTs = new Date(row.created_at || 0).getTime();
        if (currentTs >= existingTs) {
          quoteEstimateByLifecycle.set(key, row);
        }
      });
      const invoiceLifecycleKeys = new Set(
        invoices
          .map((row: any) => {
            const lifecycleRef = String(row.lifecycle_ref || '').trim();
            const orgId = String(row.organisation_id || '');
            return lifecycleRef ? `${orgId}:${lifecycleRef}` : '';
          })
          .filter(Boolean)
      );
      const quoteDocs = Array.from(quoteEstimateByLifecycle.entries())
        .filter(([key, row]) => {
          if (!(key.startsWith('doc:') || !invoiceLifecycleKeys.has(key))) return false;
          const status = String(row.status || '').toLowerCase();
          if (!(status === 'draft' || status === 'sent')) return false;
          return inRangeTs(getStatusEventTimestamp(row));
        })
        .map(([, row]) => row);
      const pipelineQuoteEstimateDocs = Array.from(quoteEstimateByLifecycle.values());
      const pipelineAcceptedDocs = pipelineQuoteEstimateDocs.filter((row: any) => String(row.status || '').toLowerCase() === 'accepted' && inRangeTs(row.accepted_at));
      const pipelineRejectedDocs = pipelineQuoteEstimateDocs.filter((row: any) => String(row.status || '').toLowerCase() === 'rejected' && inRangeTs(row.rejected_at));
      const pipelineCashDocs = pipelineQuoteEstimateDocs.filter((row: any) => String(row.status || '').toLowerCase() === 'cash' && inRangeTs(getStatusEventTimestamp(row)));
      const pipelineAcceptedRejectedDocs = [...pipelineAcceptedDocs, ...pipelineRejectedDocs];
      const pipelineNonDraftDocs = pipelineAcceptedRejectedDocs;
      const pipelineDraftSentDocs = pipelineQuoteEstimateDocs.filter((row: any) => {
        const status = String(row.status || '').toLowerCase();
        if (status === 'draft') return inRangeTs(row.draft_at);
        if (status === 'sent') return inRangeTs(row.sent_at);
        return false;
      });
      const pipelineConversionRate = pipelineNonDraftDocs.length > 0
        ? pipelineAcceptedDocs.length / pipelineNonDraftDocs.length
        : 0;
      const paidInvoices = invoices.filter((row: any) => String(row.status || '').toLowerCase() === 'paid' && inRangeTs(row.paid_at));
      const sentInvoices = invoices.filter((row: any) => {
        const status = String(row.status || '').toLowerCase();
        if (status === 'sent') return inRangeTs(row.sent_at);
        if (status === 'draft') return inRangeTs(row.draft_at);
        return false;
      });
      const unpaidInvoices = sentInvoices;
      const lifecycleProjectIdMap = new Map<string, string>();
      rows.forEach((row: any) => {
        if (row.lifecycle_ref && row.project_id) {
          lifecycleProjectIdMap.set(String(row.lifecycle_ref), String(row.project_id));
        }
      });

      // Some invoices are created long after their originating quote/estimate and may not
      // carry project_id directly. Backfill lifecycle -> project from all documents.
      const unresolvedLifecycleRefs = Array.from(
        new Set(
          [...paidInvoices, ...sentInvoices]
            .filter((row: any) => !row.project_id && row.lifecycle_ref)
            .map((row: any) => String(row.lifecycle_ref))
        )
      );
      if (unresolvedLifecycleRefs.length > 0) {
        const { data: lifecycleRows, error: lifecycleError } = await directSupabase
          .from('documents')
          .select('lifecycle_ref, project_id, created_at')
          .in('lifecycle_ref', unresolvedLifecycleRefs)
          .not('project_id', 'is', null)
          .neq('status', 'void')
          .order('created_at', { ascending: false });

        if (lifecycleError) {
          console.warn('Unable to backfill lifecycle project mappings:', lifecycleError);
        } else {
          (lifecycleRows || []).forEach((row: any) => {
            const lifecycleRef = String(row.lifecycle_ref || '');
            if (!lifecycleRef || !row.project_id) return;
            // Rows are ordered newest first; keep the first mapping encountered.
            if (!lifecycleProjectIdMap.has(lifecycleRef)) {
              lifecycleProjectIdMap.set(lifecycleRef, String(row.project_id));
            }
          });
        }
      }

      const toAmount = (value: any): number => {
        const num = Number(value ?? 0);
        return Number.isFinite(num) ? num : 0;
      };

      const paidTotal = paidInvoices.reduce((sum: number, row: any) => sum + toAmount(row.gross_total), 0);
      const sentTotal = sentInvoices.reduce((sum: number, row: any) => sum + toAmount(row.gross_total), 0);
      const unpaidTotal = unpaidInvoices.reduce((sum: number, row: any) => sum + toAmount(row.gross_total), 0);

      setInvoiceTotals({
        grandTotal: paidTotal + unpaidTotal,
        paidTotal,
        sentTotal,
        unpaidTotal,
        totalQuotes: quoteDocs.length,
        paidQuotes: 0,
        unpaidQuotes: quoteDocs.length,
        totalInvoices: paidInvoices.length + sentInvoices.length,
        paidInvoices: paidInvoices.length,
        sentInvoices: sentInvoices.length,
        unpaidInvoices: unpaidInvoices.length,
        pipelineTotalQuotesEstimates: pipelineAcceptedRejectedDocs.length + pipelineDraftSentDocs.length,
        pipelineNonDraftQuotesEstimates: pipelineNonDraftDocs.length,
        pipelineAcceptedQuotesEstimates: pipelineAcceptedDocs.length,
        pipelineRejectedQuotesEstimates: pipelineRejectedDocs.length,
        pipelineDraftSentQuotesEstimates: pipelineDraftSentDocs.length,
        pipelineCashQuotesEstimates: pipelineCashDocs.length,
        pipelineAcceptedRejectedQuotesEstimates: pipelineAcceptedRejectedDocs.length,
        pipelineConversionRate
      });

      // Fenestration summary from persisted Visual Designer quote/estimate lines.
      const fenestrationInternalCounts: Record<string, number> = {};
      const fenestrationExternalCounts: Record<string, number> = {};
      const fenestrationMaterialCounts: Record<string, number> = {};
      const fenestrationTypeCounts: Record<string, number> = {};
      const fenestrationGlassCounts: Record<string, number> = {};
      let sameDimensionOccurrences = 0;
      let withinFiftyMmDimensionOccurrences = 0;
      let windowWidthSum = 0;
      let windowHeightSum = 0;
      let windowCount = 0;
      let doorWidthSum = 0;
      let doorHeightSum = 0;
      let doorCount = 0;
      let totalOpeners = 0;
      let itemsWithOpeners = 0;
      let totalFenestrationItems = 0;
      const dimensionRows: Array<{ documentId: string; customerId: string; widthMm: number; heightMm: number }> = [];

      const addCount = (target: Record<string, number>, key: string, by = 1) => {
        const normalized = String(key || '').trim() || 'Unknown';
        target[normalized] = (target[normalized] || 0) + by;
      };

      const toFiniteNumber = (value: any) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };

      const getOccurrenceQty = (line: any) => {
        const qty = Math.max(1, Math.round(toFiniteNumber(line?.qty) || 1));
        return Number.isFinite(qty) ? qty : 1;
      };

      const parseMeta = (value: any) => {
        if (!value) return null;
        if (typeof value === 'object') return value;
        if (typeof value === 'string') {
          try {
            return JSON.parse(value);
          } catch {
            return null;
          }
        }
        return null;
      };

      // Fenestration analytics are intentionally not status-gated, but must use
      // only the active quote/estimate document per lifecycle (avoid multi-version duplication).
      const activeQuoteEstimateDocs = Array.from(quoteEstimateByLifecycle.values()).filter((doc: any) => inRangeTs(doc.created_at));
      const docByVersionId = new Map<string, any>();
      activeQuoteEstimateDocs.forEach((doc: any) => {
        const versionId = String(doc.active_version_id || '').trim();
        if (versionId) docByVersionId.set(versionId, doc);
      });
      const candidateActiveVersionIds = Array.from(
        new Set(
          activeQuoteEstimateDocs
            .map((doc: any) => String(doc.active_version_id || '').trim())
            .filter(Boolean)
        )
      );
      const candidateAcceptedVersionIds = Array.from(
        new Set(
          pipelineAcceptedDocs
            .map((doc: any) => String(doc?.active_version_id || '').trim())
            .filter(Boolean)
        )
      );
      const candidateVersionIds = Array.from(
        new Set([...candidateActiveVersionIds, ...candidateAcceptedVersionIds])
      );
      let activeVersionIdSet = new Set<string>();
      if (candidateVersionIds.length > 0) {
        const { data: activeVersionRows, error: activeVersionRowsError } = await directSupabase
          .from('document_versions')
          .select('id')
          .in('id', candidateVersionIds)
          .eq('is_active', true);
        if (activeVersionRowsError) {
          console.warn('Unable to validate active document versions for dashboard widgets:', activeVersionRowsError);
          activeVersionIdSet = new Set(candidateVersionIds);
        } else {
          activeVersionIdSet = new Set((activeVersionRows || []).map((row: any) => String(row.id || '').trim()).filter(Boolean));
        }
      }
      const activeVersionIds = candidateActiveVersionIds.filter((id) => activeVersionIdSet.has(id));
      const acceptedVersionIds = candidateAcceptedVersionIds.filter((id) => activeVersionIdSet.has(id));

      const visualDesignerLines: any[] = [];
      if (activeVersionIds.length > 0) {
        const { data: versionLines, error: versionLinesError } = await directSupabase
          .from('document_version_lines')
          .select('version_id, qty, meta_json')
          .in('version_id', activeVersionIds)
          .filter('meta_json->>feature', 'eq', 'visual_designer');
        if (versionLinesError) {
          console.warn('Unable to load versioned fenestration lines:', versionLinesError);
        } else {
          visualDesignerLines.push(...(versionLines || []));
        }
      }

      const additionalCostTypeRollup: Record<string, number> = {};
      const acceptedAdditionalCostTypeRollup: Record<string, number> = {};
      if (activeVersionIds.length > 0) {
        const { data: additionalLines, error: additionalLinesError } = await directSupabase
          .from('document_version_lines')
          .select('version_id, line_kind, additional_type, qty, unit_price, line_total, meta_json')
          .in('version_id', activeVersionIds)
          .eq('line_kind', 'other');
        if (additionalLinesError) {
          console.warn('Unable to load additional cost lines by type:', additionalLinesError);
        } else {
          (additionalLines || []).forEach((line: any) => {
            const meta = parseMeta(line?.meta_json);
            if (String(meta?.feature || '').trim() === 'visual_designer') return;
            const amountRaw = line?.line_total != null
              ? Number(line.line_total)
              : Number(line?.qty || 0) * Number(line?.unit_price || 0);
            if (!Number.isFinite(amountRaw) || amountRaw <= 0) return;
            const amount = Math.round((amountRaw + Number.EPSILON) * 100) / 100;
            const key = String(line?.additional_type || '').trim() || 'unspecified';
            additionalCostTypeRollup[key] = (additionalCostTypeRollup[key] || 0) + amount;
          });
        }
      }
      setAdditionalCostTypeTotals(additionalCostTypeRollup);

      // Accepted-only widget: strictly gate by accepted_at period via pipelineAcceptedDocs.
      // This intentionally does not depend on created_at gating used for estimated pipeline scope.
      if (acceptedVersionIds.length > 0) {
        const { data: acceptedAdditionalLines, error: acceptedAdditionalLinesError } = await directSupabase
          .from('document_version_lines')
          .select('version_id, line_kind, additional_type, qty, unit_price, line_total, meta_json')
          .in('version_id', acceptedVersionIds)
          .eq('line_kind', 'other');
        if (acceptedAdditionalLinesError) {
          console.warn('Unable to load accepted additional cost lines by type:', acceptedAdditionalLinesError);
        } else {
          (acceptedAdditionalLines || []).forEach((line: any) => {
            const meta = parseMeta(line?.meta_json);
            if (String(meta?.feature || '').trim() === 'visual_designer') return;
            const amountRaw = line?.line_total != null
              ? Number(line.line_total)
              : Number(line?.qty || 0) * Number(line?.unit_price || 0);
            if (!Number.isFinite(amountRaw) || amountRaw <= 0) return;
            const amount = Math.round((amountRaw + Number.EPSILON) * 100) / 100;
            const key = String(line?.additional_type || '').trim() || 'unspecified';
            acceptedAdditionalCostTypeRollup[key] = (acceptedAdditionalCostTypeRollup[key] || 0) + amount;
          });
        }
      }
      setAcceptedAdditionalCostTypeTotals(acceptedAdditionalCostTypeRollup);

      visualDesignerLines.forEach((line: any) => {
        const meta = parseMeta(line?.meta_json);
        if (!meta || String(meta.feature || '').trim() !== 'visual_designer') return;

        const qty = getOccurrenceQty(line);
        totalFenestrationItems += qty;

        const specs = meta.specs || {};
        const windowMaterial = meta.window_material || {};
        const windowType = meta.window_type || {};
        const windowStyle = meta.window_style || {};

        const internalFinish = String(
          specs.frame_color_internal
          || meta.presentation?.frameNameInt
          || specs.frame_color
          || 'Unknown'
        ).trim();
        const externalFinish = String(
          specs.frame_color_external
          || meta.presentation?.frameNameExt
          || specs.frame_color
          || 'Unknown'
        ).trim();
        const materialType = String(
          windowMaterial.name
          || windowMaterial.id
          || 'Unknown'
        ).trim();
        const windowTypeId = String(windowType.id || '').trim();
        const windowTypeName = String(windowType.name || '').trim();
        const windowStyleName = String(windowStyle.name || '').trim();
        const windowStyleCategory = String(windowStyle.category || '').trim();
        const typeLabel = String(
          windowTypeName
          || windowTypeId
          || windowStyleName
          || windowStyleCategory
          || 'Unknown'
        ).trim();
        const glassType = String(
          specs.glass_type
          || specs.options?.glass_style_tile
          || meta.presentation?.glassType
          || 'Unknown'
        ).trim();

        addCount(fenestrationInternalCounts, internalFinish, qty);
        addCount(fenestrationExternalCounts, externalFinish, qty);
        addCount(fenestrationMaterialCounts, materialType, qty);
        addCount(fenestrationTypeCounts, typeLabel, qty);
        addCount(fenestrationGlassCounts, glassType, qty);

        const widthMm = toFiniteNumber(specs.width_mm);
        const heightMm = toFiniteNumber(specs.height_mm);
        if (widthMm > 0 && heightMm > 0) {
          const sourceDoc = docByVersionId.get(String(line?.version_id || '').trim());
          dimensionRows.push({
            documentId: String(sourceDoc?.id || line?.version_id || ''),
            customerId: String(sourceDoc?.customer_id || ''),
            widthMm,
            heightMm
          });
        }

        const typeForMean = `${windowTypeId} ${windowTypeName}`.toLowerCase();
        const isDoor = isDoorType({ id: windowTypeId, name: windowTypeName || windowTypeId });
        const isWindow = !isDoor || /\bwindow\b|\bcasement\b|\bsash\b|\btilt\b/.test(typeForMean);
        if (widthMm > 0 && heightMm > 0) {
          if (isDoor) {
            doorWidthSum += widthMm * qty;
            doorHeightSum += heightMm * qty;
            doorCount += qty;
          }
          if (isWindow) {
            windowWidthSum += widthMm * qty;
            windowHeightSum += heightMm * qty;
            windowCount += qty;
          }
        }

        const opens = windowStyle?.templatePayload?.opens;
        if (opens && typeof opens === 'object') {
          const openerCount = Object.values(opens).filter(Boolean).length;
          if (openerCount > 0) {
            totalOpeners += openerCount * qty;
            itemsWithOpeners += qty;
          }
        }
      });

      {
        const keyForDims = (widthMm: number, heightMm: number) => `${widthMm.toFixed(3)}|${heightMm.toFixed(3)}`;
        const uniqueDimensionRows = Array.from(
          new Map(
            dimensionRows.map((row) => [
              `${row.documentId}|${row.customerId}|${keyForDims(row.widthMm, row.heightMm)}`,
              row
            ])
          ).values()
        );
        const exactGroups = new Map<string, { docs: Set<string>; indexes: number[] }>();
        uniqueDimensionRows.forEach((row, index) => {
          const key = keyForDims(row.widthMm, row.heightMm);
          const existing = exactGroups.get(key);
          if (existing) {
            existing.docs.add(row.documentId);
            existing.indexes.push(index);
            return;
          }
          exactGroups.set(key, { docs: new Set([row.documentId]), indexes: [index] });
        });
        const exactMatchedIndexes = new Set<number>();
        exactGroups.forEach((group) => {
          if (group.docs.size < 2) return;
          let hasCrossCustomerPair = false;
          for (let i = 0; i < group.indexes.length && !hasCrossCustomerPair; i += 1) {
            const a = uniqueDimensionRows[group.indexes[i]];
            for (let j = i + 1; j < group.indexes.length; j += 1) {
              const b = uniqueDimensionRows[group.indexes[j]];
              if (a.documentId !== b.documentId && a.customerId !== b.customerId) {
                hasCrossCustomerPair = true;
                break;
              }
            }
          }
          if (hasCrossCustomerPair) group.indexes.forEach((idx) => exactMatchedIndexes.add(idx));
        });
        sameDimensionOccurrences = exactMatchedIndexes.size;

        const withinFiftyMatchedIndexes = new Set<number>();
        for (let i = 0; i < uniqueDimensionRows.length; i += 1) {
          const a = uniqueDimensionRows[i];
          for (let j = i + 1; j < uniqueDimensionRows.length; j += 1) {
            const b = uniqueDimensionRows[j];
              if (a.documentId === b.documentId || a.customerId === b.customerId) continue;
            if (Math.abs(a.widthMm - b.widthMm) <= 50 && Math.abs(a.heightMm - b.heightMm) <= 50) {
              withinFiftyMatchedIndexes.add(i);
              withinFiftyMatchedIndexes.add(j);
            }
          }
        }
        withinFiftyMmDimensionOccurrences = withinFiftyMatchedIndexes.size;
      }

      setFenestrationSummary({
        internalFinishCounts: fenestrationInternalCounts,
        externalFinishCounts: fenestrationExternalCounts,
        materialTypeCounts: fenestrationMaterialCounts,
        typeCounts: fenestrationTypeCounts,
        glassTypeCounts: fenestrationGlassCounts,
        sameDimensionOccurrences,
        withinFiftyMmDimensionOccurrences,
        meanWindowWidth: windowCount > 0 ? windowWidthSum / windowCount : 0,
        meanWindowHeight: windowCount > 0 ? windowHeightSum / windowCount : 0,
        meanDoorWidth: doorCount > 0 ? doorWidthSum / doorCount : 0,
        meanDoorHeight: doorCount > 0 ? doorHeightSum / doorCount : 0,
        totalOpeners,
        itemsWithOpeners,
        totalFenestrationItems
      });

      // VAT summary using persisted DB values only (documents table fields).
      let outputVat = paidInvoices.reduce((sum: number, row: any) => {
        const vatAmount = Number(row.vat_amount ?? 0);
        return sum + (Number.isFinite(vatAmount) ? vatAmount : 0);
      }, 0);

      const paidInvoiceIds = paidInvoices
        .map((row: any) => String(row.id || '').trim())
        .filter(Boolean);
      const inputVatByInvoiceId = new Map<string, number>();
      const linesByDocumentId = new Map<string, any[]>();
      if (paidInvoiceIds.length > 0) {
        const { data: lineRows, error: lineError } = await directSupabase
          .from('document_lines')
          .select('document_id, line_kind, qty, unit_price, line_total, vat_inclusive')
          .in('document_id', paidInvoiceIds);
        if (lineError) {
          console.warn('Unable to load document lines for input VAT summary:', lineError);
        } else {
          (lineRows || []).forEach((line: any) => {
            const key = String(line.document_id || '').trim();
            if (!key) return;
            if (!linesByDocumentId.has(key)) linesByDocumentId.set(key, []);
            linesByDocumentId.get(key)!.push(line);
          });
        }

        // Fallback: some invoices may not have document_lines; use linked quote/estimate version lines.
        const invoicesMissingLines = paidInvoices.filter((invoice: any) => {
          const invoiceId = String(invoice.id || '').trim();
          return invoiceId && (linesByDocumentId.get(invoiceId) || []).length === 0;
        });
        const fallbackKeyByInvoiceId = new Map<string, string>();
        const fallbackLifecycleRefs = Array.from(
          new Set(
            invoicesMissingLines
              .map((invoice: any) => {
                const lifecycleRef = String(invoice.lifecycle_ref || '').trim();
                const orgId = String(invoice.organisation_id || '').trim();
                if (!lifecycleRef || !orgId) return '';
                const invoiceId = String(invoice.id || '').trim();
                fallbackKeyByInvoiceId.set(invoiceId, `${orgId}:${lifecycleRef}`);
                return lifecycleRef;
              })
              .filter(Boolean)
          )
        );
        if (fallbackLifecycleRefs.length > 0) {
          const { data: sourceDocs, error: sourceDocsError } = await directSupabase
            .from('documents')
            .select('id, organisation_id, lifecycle_ref, kind, active_version_id, created_at')
            .in('lifecycle_ref', fallbackLifecycleRefs)
            .in('kind', ['Quotation', 'Estimate'])
            .not('active_version_id', 'is', null)
            .neq('status', 'void')
            .order('created_at', { ascending: false });
          if (sourceDocsError) {
            console.warn('Unable to load quote/estimate versions for input VAT fallback:', sourceDocsError);
          } else {
            const sourceByKey = new Map<string, any>();
            (sourceDocs || []).forEach((doc: any) => {
              const key = `${String(doc.organisation_id || '').trim()}:${String(doc.lifecycle_ref || '').trim()}`;
              if (!key || sourceByKey.has(key)) return;
              sourceByKey.set(key, doc);
            });
            const versionIds = Array.from(
              new Set(
                Array.from(sourceByKey.values())
                  .map((doc: any) => String(doc.active_version_id || '').trim())
                  .filter(Boolean)
              )
            );
            if (versionIds.length > 0) {
              const { data: versionLines, error: versionLinesError } = await directSupabase
                .from('document_version_lines')
                .select('version_id, line_kind, qty, unit_price, line_total, vat_inclusive')
                .in('version_id', versionIds);
              if (versionLinesError) {
                console.warn('Unable to load version lines for input VAT fallback:', versionLinesError);
              } else {
                const versionLinesById = new Map<string, any[]>();
                (versionLines || []).forEach((line: any) => {
                  const key = String(line.version_id || '').trim();
                  if (!key) return;
                  if (!versionLinesById.has(key)) versionLinesById.set(key, []);
                  versionLinesById.get(key)!.push(line);
                });
                fallbackKeyByInvoiceId.forEach((sourceKey, invoiceId) => {
                  const sourceDoc = sourceByKey.get(sourceKey);
                  const versionId = String(sourceDoc?.active_version_id || '').trim();
                  if (!versionId) return;
                  const fallbackLines = versionLinesById.get(versionId) || [];
                  if (fallbackLines.length > 0) {
                    linesByDocumentId.set(invoiceId, fallbackLines);
                  }
                });
              }
            }
          }
        }
      }
      paidInvoices.forEach((invoice: any) => {
        const invoiceId = String(invoice.id || '').trim();
        if (!invoiceId) return;
        const vatPctRaw = Number(invoice.vat_pct);
        const vatPct = Number.isFinite(vatPctRaw) ? vatPctRaw : 0.2;
        const lines = linesByDocumentId.get(invoiceId) || [];
        const invoiceInputVat = lines.reduce((sum: number, line: any) => {
          const lineKind = String(line.line_kind || '').toLowerCase();
          if (lineKind === 'labour') return sum;
          const grossAmount = line.line_total != null
            ? Number(line.line_total)
            : Number(line.qty || 0) * Number(line.unit_price || 0);
          if (!Number.isFinite(grossAmount) || grossAmount <= 0) return sum;
          const vatInclusive = !!line.vat_inclusive;
          const vatPart = vatInclusive
            ? grossAmount * (vatPct / (1 + vatPct))
            : grossAmount * vatPct;
          return sum + (Number.isFinite(vatPart) ? vatPart : 0);
        }, 0);
        inputVatByInvoiceId.set(invoiceId, Math.round((invoiceInputVat + Number.EPSILON) * 100) / 100);
      });
      let inputVat = paidInvoices.reduce((sum: number, row: any) => {
        const invoiceId = String(row.id || '').trim();
        return sum + Number(inputVatByInvoiceId.get(invoiceId) || 0);
      }, 0);

      outputVat = Math.round((outputVat + Number.EPSILON) * 100) / 100;
      inputVat = Math.round((inputVat + Number.EPSILON) * 100) / 100;
      const netVatPosition = Math.round((outputVat - inputVat + Number.EPSILON) * 100) / 100;
      setVatSummary({ inputVat, outputVat, netVatPosition });
      setVatWorkingsRows(
        paidInvoices.map((row: any) => {
          const output = Math.round(((Number(row.vat_amount ?? 0) || 0) + Number.EPSILON) * 100) / 100;
          const input = Number(inputVatByInvoiceId.get(String(row.id || '').trim()) || 0);
          const ref =
            String(row.lifecycle_ref || '').trim() ||
            String(row.title || '').trim() ||
            String(row.id || '').slice(0, 8).toUpperCase();
          const customerName = `${String(row.customer_first_name || '').trim()} ${String(row.customer_last_name || '').trim()}`.trim() || '—';
          return {
            documentId: String(row.id || ''),
            ref,
            customerName,
            inputVat: input,
            outputVat: output,
            netOff: Math.round((output - input + Number.EPSILON) * 100) / 100
          };
        })
      );

      const projectIds = Array.from(
        new Set(
          [...paidInvoices, ...sentInvoices]
            .map((row: any) => row.project_id || lifecycleProjectIdMap.get(String(row.lifecycle_ref || '')))
            .filter((projectId: any): projectId is string => Boolean(projectId))
        )
      );

      if (projectIds.length === 0) {
        setRevenueByCategoryByStatus({
          paid: {},
          sent: {}
        });
        setCategoryLabelsByKey({});
        setRevenueByPostcodeByStatus({ paid: {}, sent: {} });
        setProjectsByPostcodeByStatus({ paid: {}, sent: {} });
        return;
      }

      const { data: projectRows, error: projectsError } = await directSupabase
        .from('projects')
        .select('*')
        .in('id', projectIds);

      if (projectsError) {
        throw new Error(projectsError.message || 'Failed to fetch projects for invoice breakdowns');
      }

      const projectById = new Map<string, any>((projectRows || []).map((project: any) => [project.id, project]));

      const customerIds = Array.from(
        new Set(
          [...paidInvoices, ...sentInvoices]
            .map((invoice: any) => invoice.customer_id)
            .filter((customerId: any): customerId is string => Boolean(customerId))
        )
      );
      let customerById = new Map<string, any>();

      if (customerIds.length > 0) {
        const { data: customerRows, error: customersError } = await directSupabase
          .from('customers')
          .select('id, address, billing_address')
          .in('id', customerIds);

        if (customersError) {
          console.warn('Unable to fetch customer addresses for postcode mapping:', customersError);
        } else {
          customerById = new Map<string, any>((customerRows || []).map((customer: any) => [customer.id, customer]));
        }
      }
      const nextCategoryLabelsByKey: Record<string, string> = {};
      const aggregateByStatus = (invoicesForStatus: any[]) => {
        const nextRevenueByCategory: CategoryBreakdown = {};
        const nextRevenueByPostcode: Record<string, number> = {};
        const nextProjectsByPostcode: Record<string, { inProgress: number; complete: number }> = {};

        invoicesForStatus.forEach((invoice: any) => {
          const resolvedProjectId = invoice.project_id || lifecycleProjectIdMap.get(String(invoice.lifecycle_ref || ''));
          const project = projectById.get(resolvedProjectId);
          const customer = customerById.get(invoice.customer_id);
          const categories = getProjectCategoryEntries(project);
          const splitCount = categories.length || 1;
          const revenueSplit = toAmount(invoice.gross_total) / splitCount;
          categories.forEach(({ key, label }) => {
            nextCategoryLabelsByKey[key] = nextCategoryLabelsByKey[key] || label;
            nextRevenueByCategory[key] = (nextRevenueByCategory[key] || 0) + revenueSplit;
          });

          const fullAddress =
            project?.customer_address ||
            project?.address ||
            customer?.address ||
            customer?.billing_address ||
            '';
          const fullPostcode = extractPostcodeFromAddress(fullAddress);
          const postcodeArea = extractPostcodeArea(fullPostcode || 'Unknown');
          nextRevenueByPostcode[postcodeArea] = (nextRevenueByPostcode[postcodeArea] || 0) + toAmount(invoice.gross_total);
          nextProjectsByPostcode[postcodeArea] = nextProjectsByPostcode[postcodeArea] || { inProgress: 0, complete: 0 };
          nextProjectsByPostcode[postcodeArea].complete += 1;
        });

        return { nextRevenueByCategory, nextRevenueByPostcode, nextProjectsByPostcode };
      };

      const paidAggregates = aggregateByStatus(paidInvoices);
      const sentAggregates = aggregateByStatus(sentInvoices);

      setRevenueByCategoryByStatus({
        paid: paidAggregates.nextRevenueByCategory,
        sent: sentAggregates.nextRevenueByCategory
      });
      setCategoryLabelsByKey(nextCategoryLabelsByKey);
      setRevenueByPostcodeByStatus({
        paid: paidAggregates.nextRevenueByPostcode,
        sent: sentAggregates.nextRevenueByPostcode
      });
      setProjectsByPostcodeByStatus({
        paid: paidAggregates.nextProjectsByPostcode,
        sent: sentAggregates.nextProjectsByPostcode
      });
    } catch (err) {
      console.error('Error fetching invoice totals:', err);
      setRevenueByCategoryByStatus({
        paid: {},
        sent: {}
      });
      setCategoryLabelsByKey({});
      setRevenueByPostcodeByStatus({ paid: {}, sent: {} });
      setProjectsByPostcodeByStatus({ paid: {}, sent: {} });
      setAdditionalCostTypeTotals({});
      setAcceptedAdditionalCostTypeTotals({});
      setVatSummary({ inputVat: 0, outputVat: 0, netVatPosition: 0 });
      setVatWorkingsRows([]);
    }
  };

  // Extract postcode area from full postcode
  const extractPostcodeArea = (postcode: string): string => {
    if (!postcode) return 'Unknown';
    // Extract first part of postcode (e.g., "PO21" from "PO21 1JU")
    const match = postcode.match(/^[A-Z]{1,2}[0-9][0-9A-Z]?/);
    return match ? match[0].toUpperCase() : 'Unknown';
  };

  // Fetch projects data
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use secure API to fetch projects
      const { data, error } = await directSupabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });
      
      if (error) {
        throw new Error(error.message || 'Failed to fetch projects');
      } else {
        // Get all projects for completed count (no time filter)
        const allProjects = data || [];
        
        // Apply time filter to the fetched data for other stats
        const range = getDateRangeForTimeRange(timeRange);

        // Filter projects by date for time-based stats
        const filteredProjects = allProjects.filter((project: Project) => {
          const projectDate = new Date(project.created_at);
          return projectDate >= range.from && projectDate < range.to;
        });

        setProjects(filteredProjects);
        calculateStats(filteredProjects, allProjects, range); // Pass both filtered and all projects
        // Stage counts use the Workspaces Board's answer for each job
        loadWorkspaceSnapshots(filteredProjects as any)
          .then((snapshots) => {
            const byStage: Record<string, number> = {};
            Object.values(snapshots).forEach((snapshot) => {
              byStage[snapshot.stage] = (byStage[snapshot.stage] || 0) + 1;
            });
            setProjectStats((prev: any) => ({ ...prev, byStage }));
          })
          .catch((err) => console.warn('Failed to load workspace stages', err));
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  // Calculate project statistics
  const calculateStats = (projectData: Project[], allProjects: Project[], range: { from: Date; to: Date }) => {
    const stats = {
      total: projectData.length,
      byStage: {} as Record<string, number>,
      byMonth: [] as any[],
      byCustomer: [] as any[],
      totalValue: 0,
      avgValue: 0,
      recentActivity: 0,
      completedProjects: 0,
      activeProjects: 0,
      cancelledProjects: 0,
      supplyOnlyProjects: 0
    };

    // Stage counts (byStage) are filled in from the workspace records once loaded
    projectData.forEach(project => {
      const lifecycle = workspaceLifecycleStatus(project);
      const isCompleted = lifecycle === 'Completed';
      const isCancelled = lifecycle === 'Cancelled';
      if (project.supply_only) {
        stats.supplyOnlyProjects += 1;
      }
      

      
      // Count completed vs active projects (use allProjects for completed count, projectData for active count)
      if (isCompleted) {
        // Don't count here - we'll count from allProjects separately
      } else if (!isCancelled) {
        stats.activeProjects += 1;
      }
      
      // Calculate total value from supplier prices
      if (project.supplier_prices && project.supplier_prices.length > 0) {
        const projectValue = project.supplier_prices.reduce((sum, price) => {
          return sum + (Number(price.price) || 0);
        }, 0);
        stats.totalValue += projectValue;
      }
    });

    // Calculate average value
    stats.avgValue = stats.total > 0 ? stats.totalValue / stats.total : 0;

    // Group by month
    const monthlyData = projectData.reduce((acc: any, project) => {
      const date = new Date(project.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!acc[monthKey]) {
        acc[monthKey] = {
          month: monthKey,
          count: 0,
          value: 0
        };
      }
      
      acc[monthKey].count += 1;
      
      // Calculate project value
      if (project.supplier_prices && project.supplier_prices.length > 0) {
        const projectValue = project.supplier_prices.reduce((sum, price) => {
          return sum + (Number(price.price) || 0);
        }, 0);
        acc[monthKey].value += projectValue;
      }
      
      return acc;
    }, {});

    stats.byMonth = Object.values(monthlyData).sort((a: any, b: any) => a.month.localeCompare(b.month));

    // Group by customer
    const customerData = projectData.reduce((acc: any, project) => {
      const customerName = project.customer_name || 'Unknown';
      
      if (!acc[customerName]) {
        acc[customerName] = {
          name: customerName,
          count: 0,
          value: 0
        };
      }
      
      acc[customerName].count += 1;
      
      // Calculate project value
      if (project.supplier_prices && project.supplier_prices.length > 0) {
        const projectValue = project.supplier_prices.reduce((sum, price) => {
          return sum + (Number(price.price) || 0);
        }, 0);
        acc[customerName].value += projectValue;
      }
      
      return acc;
    }, {});

    stats.byCustomer = Object.values(customerData)
      .sort((a: any, b: any) => b.count - a.count)
      .slice(0, 10); // Top 10 customers

    // Recent activity (last 7 days)
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    stats.recentActivity = projectData.filter(p => 
      new Date(p.created_at) > weekAgo
    ).length;

    // Count completed projects based on explicit lifecycle timestamp.
    const rangeFromTs = range.from.getTime();
    const rangeToTs = range.to.getTime();
    stats.completedProjects = allProjects.filter((project: any) => {
      const completedTs = getLifecycleTimestamp(project.completed_at);
      if (completedTs == null) return false;
      return completedTs >= rangeFromTs && completedTs < rangeToTs;
    }).length;

    // Count cancelled projects based on explicit lifecycle timestamp.
    stats.cancelledProjects = allProjects.filter((project: any) => {
      const cancelledTs = getLifecycleTimestamp(project.cancelled_at);
      if (cancelledTs == null) return false;
      return cancelledTs >= rangeFromTs && cancelledTs < rangeToTs;
    }).length;
    

    setProjectStats(stats);
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value);
  };

  // Format number
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-GB').format(value);
  };

  const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;
  const formatMeanDimensions = (width: number, height: number) => {
    if (!(width > 0 && height > 0)) return '—';
    return `${formatNumber(Math.round(width))} x ${formatNumber(Math.round(height))} mm`;
  };

  const getBreakdownEntries = (counts: Record<string, number>) => {
    return Object.entries(counts || {})
      .filter(([, count]) => Number(count) > 0)
      .sort((a, b) => b[1] - a[1]);
  };

  const getTopBreakdown = (counts: Record<string, number>, limit = 4) => {
    return getBreakdownEntries(counts)
      .slice(0, limit)
      .map(([label, count]) => `${label} (${formatNumber(count)})`)
      .join(', ');
  };

  const getTopBreakdownItems = (counts: Record<string, number>, limit = 4) => {
    return getBreakdownEntries(counts)
      .slice(0, limit)
      .map(([label, count]) => `${label} (${formatNumber(count)})`);
  };

  const renderFenestrationSubtitle = (counts: Record<string, number>, emptyLabel: string) => {
    if (!getTopBreakdown(counts)) {
      return 'No Visual Designer items in selected period';
    }

    const topItems = getTopBreakdownItems(counts);

    return (
      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
        {(topItems.length > 0 ? topItems : [emptyLabel]).map((item) => (
          <Box component="li" key={item} sx={{ mb: 0.25 }}>
            {item}
          </Box>
        ))}
        <Box component="li">{`${formatNumber(fenestrationSummary.totalFenestrationItems)} total items`}</Box>
      </Box>
    );
  };

  // Prepare pie chart data
  const getStageChartData = () => {
    // Every stage, including those with 0 projects
    return WORKSPACE_STAGE_LIST.map((stage) => ({
      name: stage.label,
      value: projectStats.byStage?.[stage.id] || 0,
      color: stageColors[stage.id],
    }));
  };

  // Prepare monthly chart data
  const getMonthlyChartData = () => {
    return (projectStats.byMonth || []).map((item: any) => ({
      month: item.month,
      projects: item.count,
      value: item.value
    }));
  };

  // Prepare customer chart data
  const getCustomerChartData = () => {
    return (projectStats.byCustomer || []).map((item: any) => ({
      name: item.name,
      projects: item.count,
      value: item.value
    }));
  };

  const renderCustomerAxisTick = ({ x, y, payload }: any) => {
    const customerName = String(payload?.value || '').trim();
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="start"
          transform="rotate(55)"
          fill="var(--joy-palette-primary-600)"
          style={{
            cursor: customerName ? 'pointer' : 'default',
            textDecoration: customerName ? 'underline' : 'none',
            fontWeight: 500
          }}
          onClick={() => {
            if (!customerName) return;
            navigate(`/customers/${encodeURIComponent(customerName)}`);
          }}
        >
          {customerName}
        </text>
      </g>
    );
  };

  // Prepare revenue by category chart data
  const getRevenueByCategoryChartData = (statusView: InvoiceStatusView) => {
    const data = revenueByCategoryByStatus[statusView];
    const toColor = (key: string) => {
      let hash = 0;
      for (let i = 0; i < key.length; i += 1) hash = (hash << 5) - hash + key.charCodeAt(i);
      const hue = Math.abs(hash) % 360;
      return `hsl(${hue}, 65%, 55%)`;
    };
    return Object.entries(data || {})
      .map(([key, value]) => ({
        key,
        name: categoryLabelsByKey[key] || (key === 'undisclosed' ? 'Undisclosed' : key),
        value,
        color: toColor(key),
        filterValue: key === 'undisclosed' ? 'undisclosed' : `product:${key}`
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
  };

  // Prepare revenue by postcode chart data
  const getRevenueByPostcodeChartData = (statusView: InvoiceStatusView) => {
    const sortedPostcodes = Object.entries(revenueByPostcodeByStatus[statusView] || {})
      .sort(([,a], [,b]) => b - a) // Sort by revenue descending
      .slice(0, 10); // Top 10 postcodes

    return sortedPostcodes.map(([postcode, revenue]) => ({
      name: postcode,
      value: revenue,
      color: `hsl(${Math.random() * 360}, 70%, 50%)` // Random colors for variety
    }));
  };

  // Prepare projects by postcode chart data
  const getProjectsByPostcodeChartData = (statusView: InvoiceStatusView) => {
    const sortedPostcodes = Object.entries(projectsByPostcodeByStatus[statusView] || {})
      .sort(([,a], [,b]) => {
        const aTotal = a.inProgress + a.complete;
        const bTotal = b.inProgress + b.complete;
        return bTotal - aTotal; // Sort by total projects descending
      })
      .slice(0, 10); // Top 10 postcodes

    return sortedPostcodes.map(([postcode, counts]) => {
      return {
        postcode,
        inProgress: counts.inProgress,
        complete: counts.complete,
        total: counts.inProgress + counts.complete
      };
    }).filter(item => item.total > 0);
  };

  const toAdditionalCostTypeChartData = (totalsByType: Record<string, number>) => {
    const labelByKey: Record<string, string> = {
      glass: 'Glass',
      window: 'Window(s)',
      door: 'Door(s)',
      window_door: 'Window(s) & Door(s)',
      window_glass: 'Window(s) & Glass',
      door_glass: 'Door(s) & Glass',
      other: 'Other',
      unspecified: 'Unspecified'
    };
    const colorByKey: Record<string, string> = {
      glass: '#1f77b4',
      window: '#2ca02c',
      door: '#ff7f0e',
      window_door: '#d62728',
      window_glass: '#9467bd',
      door_glass: '#8c564b',
      other: '#17becf',
      unspecified: '#7f7f7f'
    };

    return Object.entries(totalsByType || {})
      .map(([key, value]) => ({
        key,
        name: labelByKey[key] || key,
        value: Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100,
        color: colorByKey[key] || '#8884d8'
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
  };

  const getAdditionalCostTypeChartData = () => toAdditionalCostTypeChartData(additionalCostTypeTotals);
  const getAcceptedAdditionalCostTypeChartData = () => toAdditionalCostTypeChartData(acceptedAdditionalCostTypeTotals);

  const getAdditionalTypeGroupedRows = (data: Array<{ key: string; name: string; value: number }>) => {
    const byKey = new Map(data.map((item) => [item.key, item]));
    const fenestrationKeys = ['window', 'window_glass', 'door_glass', 'window_door'];
    const fenestrationTotal = fenestrationKeys.reduce((sum, key) => sum + Number(byKey.get(key)?.value || 0), 0);

    const rows: Array<{ label: string; value: number; drilldownType: string }> = [];

    if (fenestrationTotal > 0) {
      rows.push({
        label: 'Fenestration',
        value: Math.round((fenestrationTotal + Number.EPSILON) * 100) / 100,
        drilldownType: fenestrationKeys.join(',')
      });
    }

    const distinctRows: Array<{ key: string; label: string }> = [
      { key: 'door', label: 'Door(s)' },
      { key: 'glass', label: 'Glass' },
      { key: 'other', label: 'Other' },
      { key: 'unspecified', label: 'Unspecified' }
    ];

    distinctRows.forEach(({ key, label }) => {
      const value = Number(byKey.get(key)?.value || 0);
      if (value <= 0) return;
      rows.push({
        label,
        value: Math.round((value + Number.EPSILON) * 100) / 100,
        drilldownType: key
      });
    });

    return rows;
  };

  const getAdditionalTypeOriginalRows = (data: Array<{ key: string; name: string; value: number }>) => {
    const order: Record<string, number> = {
      window: 1,
      window_glass: 2,
      door_glass: 3,
      window_door: 4,
      door: 5,
      glass: 6,
      other: 7,
      unspecified: 8
    };

    return [...data]
      .sort((a, b) => {
        const aOrder = order[a.key] ?? 999;
        const bOrder = order[b.key] ?? 999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return b.value - a.value;
      })
      .map((item) => ({
        label: item.name,
        value: item.value,
        drilldownType: item.key
      }));
  };

  // Stat Card Component
  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', onClick, flatDetails = false }: {
    title: string;
    value?: string;
    subtitle?: React.ReactNode;
    icon: any;
    color?: 'primary' | 'success' | 'warning' | 'danger';
    onClick?: () => void;
    flatDetails?: boolean;
  }) => (
    <Card 
      variant="outlined" 
      sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow 0.2s', '&:hover': onClick ? { boxShadow: 'md', backgroundColor: 'background.level1' } : {} }}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      onKeyDown={onClick ? (e: React.KeyboardEvent<HTMLDivElement>) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: '50%',
              bgcolor: `${color}.solidBg`,
              color: `${color}.solidColor`,
              boxShadow: 'sm'
            }}
          >
            <Icon size={20} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography level="body-sm" color="neutral">
              {title}
            </Typography>
            {!flatDetails ? (
              <Typography level="h4" sx={{ fontWeight: 'bold' }}>
                {value}
              </Typography>
            ) : (
              value ? (
                <Typography level="body-sm" color="neutral">
                  {value}
                </Typography>
              ) : null
            )}
            {subtitle && (
              typeof subtitle === 'string'
                ? (
                  <Typography level={flatDetails ? 'body-sm' : 'body-xs'} color="neutral">
                    {subtitle}
                  </Typography>
                )
                : (
                  <Box sx={{ mt: 0.25, color: 'text.secondary', fontSize: flatDetails ? '0.875rem' : '0.75rem' }}>
                    {subtitle}
                  </Box>
                )
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const getInvoiceStatusLabel = (statusView: InvoiceStatusView) => (statusView === 'paid' ? 'Paid' : 'Unpaid');

  const getInvoiceMetricsForStatusView = (statusView: InvoiceStatusView) => {
    const selectedLabel = getInvoiceStatusLabel(statusView);
    const otherLabel = statusView === 'paid' ? 'Unpaid' : 'Paid';
    const selectedTotal = statusView === 'paid' ? invoiceTotals.paidTotal : invoiceTotals.sentTotal;
    const selectedCount = statusView === 'paid' ? invoiceTotals.paidInvoices : invoiceTotals.sentInvoices;
    const otherTotal = statusView === 'paid' ? invoiceTotals.sentTotal : invoiceTotals.paidTotal;
    const otherCount = statusView === 'paid' ? invoiceTotals.sentInvoices : invoiceTotals.paidInvoices;
    return { selectedLabel, otherLabel, selectedTotal, selectedCount, otherTotal, otherCount };
  };

  const activeRange = useMemo(() => getDateRangeForTimeRange(timeRange), [timeRange]);
  const activeRangeQuery = useMemo(
    () => buildDashboardRangeQuery({ from: activeRange.from, to: activeRange.to, period: timeRange }),
    [activeRange, timeRange]
  );

  const getDocsInvoiceFilterPath = (statusView: InvoiceStatusView) => {
    return buildDocsInvoiceFilterPath(activeRangeQuery, statusView);
  };

  const getDocsQuoteEstimateFilterPath = () => {
    return buildDocsQuoteEstimateFilterPath(activeRangeQuery);
  };

  const getDocsQuoteEstimateStatusFilterPath = (status: 'all' | 'accepted' | 'rejected' | 'cash') => {
    return buildDocsQuoteEstimateStatusFilterPath(activeRangeQuery, status);
  };

  const getDocsQuoteEstimateAwaitingFilterPath = () => {
    return buildDocsQuoteEstimateAwaitingFilterPath(activeRangeQuery);
  };

  const getDocsVatFilterPath = (type: 'input' | 'output') => {
    return buildDocsVatFilterPath(activeRangeQuery, type);
  };

  const getFenestrationDrilldownPath = (metric: string) => {
    return buildFenestrationDrilldownPath(activeRangeQuery, metric);
  };

  const getAdditionalCostsDrilldownPath = (mode: 'estimated' | 'accepted', type: string) => {
    return buildAdditionalCostsDrilldownPath(activeRangeQuery, { mode, type });
  };

  const InvoiceStatusToggle = ({ value, onChange }: { value: InvoiceStatusView; onChange: (value: InvoiceStatusView) => void }) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        size="sm"
        variant={value === 'paid' ? 'solid' : 'outlined'}
        color="success"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onChange('paid');
        }}
      >
        Paid
      </Button>
      <Button
        size="sm"
        variant={value === 'sent' ? 'solid' : 'outlined'}
        color="primary"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onChange('sent');
        }}
      >
        Unpaid
      </Button>
    </Box>
  );

  // Load data on mount and when time range changes
  useEffect(() => {
    fetchProjects();
    fetchInvoiceTotals();
  }, [timeRange]);

  useEffect(() => {
    try {
      sessionStorage.setItem(DASHBOARD_TIME_RANGE_SESSION_KEY, timeRange);
    } catch {
      // Ignore storage write failures.
    }
  }, [timeRange]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  const estimatedAdditionalCostTypeData = getAdditionalCostTypeChartData();
  const acceptedAdditionalCostTypeData = getAcceptedAdditionalCostTypeChartData();
  const estimatedAdditionalGrandTotal = estimatedAdditionalCostTypeData.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const acceptedAdditionalGrandTotal = acceptedAdditionalCostTypeData.reduce((sum, item) => sum + Number(item.value || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconWrapper icon={FaChartBar} width={24} height={24} />
          <Typography level="h1">
            Project Dashboard
          </Typography>
        </Stack>
        <FormControl size="sm" sx={{ minWidth: 120 }}>
          <FormLabel>Time Range</FormLabel>
          <Select
            value={timeRange}
            onChange={(_, value) => setTimeRange((value as DashboardTimeRange) || 'this_month')}
          >
            <Option value="this_month">This Month</Option>
            <Option value="last_month">Last Month</Option>
            <Option value="this_quarter">This Quarter</Option>
            <Option value="last_quarter">Last Quarter</Option>
            <Option value="this_year">This Year</Option>
            <Option value="last_year">Last Year</Option>
            <Option value="this_financial_year">This Financial Year</Option>
            <Option value="last_financial_year">Last Financial Year</Option>
            <Option value="ytd">Year to Date (YTD)</Option>
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Typography level="h3" sx={{ mb: 2 }}>
        Financial Summary
      </Typography>

      {/* Financial Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Paid Invoices"
            value={formatCurrency(invoiceTotals.paidTotal)}
            subtitle={`${invoiceTotals.paidInvoices} paid invoices`}
            icon={FaCheckCircle}
            color="success"
            onClick={() => navigate(getDocsInvoiceFilterPath('paid'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Unpaid Invoices"
            value={formatCurrency(invoiceTotals.sentTotal)}
            subtitle={`${invoiceTotals.sentInvoices} unpaid invoices`}
            icon={FaClock}
            color="warning"
            onClick={() => navigate(getDocsInvoiceFilterPath('sent'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Quotes & Estimates"
            value={formatNumber(invoiceTotals.totalQuotes)}
            subtitle="Not yet accepted"
            icon={FaClipboardList}
            color="primary"
            onClick={() => navigate(getDocsQuoteEstimateFilterPath())}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Input VAT"
            value={formatCurrency(vatSummary.inputVat)}
            subtitle="Paid invoices • non-labour line VAT"
            icon={FaBoxes}
            color="warning"
            onClick={() => navigate(getDocsVatFilterPath('input'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Output VAT"
            value={formatCurrency(vatSummary.outputVat)}
            subtitle="Stored invoice VAT across paid invoices"
            icon={FaDollarSign}
            color="primary"
            onClick={() => navigate(getDocsVatFilterPath('output'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Net VAT Position"
            value={formatCurrency(vatSummary.netVatPosition)}
            subtitle={vatSummary.netVatPosition >= 0 ? 'Positive = owed to HMRC' : 'Negative = VAT refund due'}
            icon={FaChartLine}
            color={vatSummary.netVatPosition >= 0 ? 'warning' : 'success'}
            onClick={() => setVatWorkingsOpen(true)}
          />
        </Grid>
      </Grid>

      <Typography level="h3" sx={{ mb: 2 }}>
        Conversion Tracking
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Accepted Quotes/Estimates"
            value={formatNumber(invoiceTotals.pipelineAcceptedQuotesEstimates)}
            subtitle="Status = Accepted"
            icon={FaCheckCircle}
            color="success"
            onClick={() => navigate(getDocsQuoteEstimateStatusFilterPath('accepted'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Rejected Quotes/Estimates"
            value={formatNumber(invoiceTotals.pipelineRejectedQuotesEstimates)}
            subtitle="Status = Rejected"
            icon={FaExclamationTriangle}
            color="danger"
            onClick={() => navigate(getDocsQuoteEstimateStatusFilterPath('rejected'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Quote/Estimate Conversion Rate"
            value={formatPercent(invoiceTotals.pipelineConversionRate)}
            subtitle={`${formatNumber(invoiceTotals.pipelineAcceptedQuotesEstimates)} of ${formatNumber(invoiceTotals.pipelineNonDraftQuotesEstimates)} converted (Accepted + Rejected)`}
            icon={FaChartLine}
            color="primary"
            onClick={() => navigate(getDocsQuoteEstimateStatusFilterPath('all'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Draft + Sent Quotes/Estimates"
            value={formatNumber(invoiceTotals.pipelineDraftSentQuotesEstimates)}
            subtitle={`${formatNumber(invoiceTotals.pipelineAcceptedQuotesEstimates)} Accepted, ${formatNumber(invoiceTotals.pipelineRejectedQuotesEstimates)} Rejected, ${formatNumber(invoiceTotals.pipelineDraftSentQuotesEstimates)} Awaiting Final Status`}
            icon={FaClock}
            color="warning"
            onClick={() => navigate(getDocsQuoteEstimateAwaitingFilterPath())}
          />
        </Grid>
      </Grid>
      <Typography level="h3" sx={{ mb: 2 }}>
        Invoices
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={3}>
          <InvoiceStatusScoped>
            {({ statusView, setStatusView }) => {
              const metrics = getInvoiceMetricsForStatusView(statusView);
              return (
                <Card
                  variant="outlined"
                  sx={{ height: '100%', cursor: 'pointer', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 'md', backgroundColor: 'background.level1' } }}
                  onClick={() => navigate(getDocsInvoiceFilterPath(statusView))}
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(getDocsInvoiceFilterPath(statusView)); } }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'primary.solidBg', color: 'primary.solidColor', boxShadow: 'sm' }}>
                          <IconWrapper icon={FaDollarSign} width={20} height={20} />
                        </Box>
                        <Typography level="body-sm" color="neutral">
                          Total Invoices
                        </Typography>
                      </Stack>
                      <InvoiceStatusToggle value={statusView} onChange={setStatusView} />
                    </Box>
                    <Typography level="h4" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(metrics.selectedCount)}
                    </Typography>
                    <Typography level="body-xs" color="neutral">
                      {metrics.selectedLabel} invoices
                    </Typography>
                  </CardContent>
                </Card>
              );
            }}
          </InvoiceStatusScoped>
        </Grid>
      </Grid>

      <Typography level="h3" sx={{ mb: 2 }}>
        Quotes
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Total Quotes/Estimates"
            value={formatNumber(invoiceTotals.totalQuotes)}
            subtitle="Not yet accepted"
            icon={FaClipboardList}
            color="primary"
            onClick={() => navigate(getDocsQuoteEstimateFilterPath())}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Cash"
            value={formatNumber(invoiceTotals.pipelineCashQuotesEstimates)}
            subtitle="Status = Cash"
            icon={FaDollarSign}
            color="primary"
            onClick={() => navigate(getDocsQuoteEstimateStatusFilterPath('cash'))}
          />
        </Grid>
      </Grid>

      <Typography level="h3" sx={{ mb: 2 }}>
        Projects Section
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Completed Projects"
            value={formatNumber(projectStats.completedProjects || 0)}
            subtitle="Finished"
            icon={FaCheckCircle}
            color="success"
            onClick={() => {
              navigate(buildProjectsFilterPath(activeRangeQuery, { progress: 6 }));
            }}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Cancelled Projects"
            value={formatNumber(projectStats.cancelledProjects || 0)}
            subtitle="Cancelled this period"
            icon={FaExclamationTriangle}
            color="danger"
            onClick={() => {
              navigate(buildProjectsFilterPath(activeRangeQuery, { status: 'cancelled' }));
            }}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Total Projects"
            value={formatNumber(projectStats.total || 0)}
            subtitle={activeRange.label}
            icon={FaClipboardList}
            color="primary"
            onClick={() => {
              navigate(buildProjectsFilterPath(activeRangeQuery, {}));
            }}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Supply Only"
            value={formatNumber(projectStats.supplyOnlyProjects || 0)}
            subtitle="Supply only this period"
            icon={FaBoxes}
            color="primary"
            onClick={() => {
              navigate(buildProjectsFilterPath(activeRangeQuery, { supply_only: true }));
            }}
          />
        </Grid>
      </Grid>



      {/* Charts Grid */}
      <Grid container spacing={3}>
        <Grid xs={12} sx={{ order: 1 }}>
          <Typography level="h4">Financial Insights</Typography>
        </Grid>

        {/* Projects by Workflow Stage */}
        <Grid xs={12} lg={6} sx={{ order: 5 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Projects by Workflow Stage
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={getStageChartData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getStageChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => [`${value} projects`, 'Projects']} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Projects Trend */}
        <Grid xs={12} lg={6} sx={{ order: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Monthly Projects Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={getMonthlyChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: any, name: any) => [
                      name === 'projects' ? `${value} projects` : formatCurrency(value),
                      name === 'projects' ? 'Projects' : 'Value'
                    ]}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="projects"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.3}
                    name="projects"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Customers */}
        <Grid xs={12} lg={6} sx={{ order: 10 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Number of Projects Per Customer
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getCustomerChartData()} margin={{ top: 8, right: 16, left: 0, bottom: 28 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" height={100} tickMargin={10} interval={0} tick={renderCustomerAxisTick} />
                  <YAxis allowDecimals={false} />
                  <Tooltip formatter={(value: any) => [`${value} projects`, 'Projects']} />
                  <Bar dataKey="projects" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Projects vs Value */}
        <Grid xs={12} lg={6} sx={{ order: 7 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Projects vs Value
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={getMonthlyChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip 
                    formatter={(value: any, name: any) => [
                      name === 'projects' ? `${value} projects` : formatCurrency(value),
                      name === 'projects' ? 'Projects' : 'Value'
                    ]}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="projects" fill="#8884d8" name="projects" />
                  <Line yAxisId="right" type="monotone" dataKey="value" stroke="#82ca9d" name="value" />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Workflow Stage Distribution Bar Chart */}
        <Grid xs={12} lg={6} sx={{ order: 8 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Workflow Stage Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getStageChartData()} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip formatter={(value: any) => [`${value} projects`, 'Projects']} />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice Status Distribution */}
        <Grid xs={12} lg={6} sx={{ order: 2 }}>
          <InvoiceStatusScoped>
            {({ statusView, setStatusView }) => {
              const metrics = getInvoiceMetricsForStatusView(statusView);
              return (
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography level="title-lg">
                        Invoice Status Distribution
                      </Typography>
                      <InvoiceStatusToggle value={statusView} onChange={setStatusView} />
                    </Box>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: metrics.selectedLabel, value: metrics.selectedCount, color: '#82ca9d' },
                            { name: metrics.otherLabel, value: metrics.otherCount, color: '#ffc658' },
                            { name: 'Quotes & Estimates', value: invoiceTotals.totalQuotes, color: '#8884d8' }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {[
                            { name: metrics.selectedLabel, value: metrics.selectedCount, color: '#82ca9d' },
                            { name: metrics.otherLabel, value: metrics.otherCount, color: '#ffc658' },
                            { name: 'Quotes & Estimates', value: invoiceTotals.totalQuotes, color: '#8884d8' }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: any) => [`${value} documents`, 'Documents']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              );
            }}
          </InvoiceStatusScoped>
        </Grid>

        {/* Revenue by Category */}
        <Grid xs={12} lg={6} sx={{ order: 3 }}>
          <InvoiceStatusScoped>
            {({ statusView, setStatusView }) => {
              const metrics = getInvoiceMetricsForStatusView(statusView);
              const chartData = getRevenueByCategoryChartData(statusView);
              return (
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography level="title-lg">
                        {metrics.selectedLabel} Revenue by Category
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <InvoiceStatusToggle value={statusView} onChange={setStatusView} />
                      </Box>
                    </Box>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          onClick={(data) => {
                            if (data && (data as any).filterValue) {
                              navigate(
                                buildProjectsFilterPath(activeRangeQuery, { category: (data as any).filterValue })
                              );
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value: any) => [formatCurrency(value), 'Revenue']} 
                          labelFormatter={(label) => `${label} Revenue`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              );
            }}
          </InvoiceStatusScoped>
        </Grid>

        {/* Revenue by Postcode Area */}
        <Grid xs={12} lg={6} sx={{ order: 11 }}>
          <InvoiceStatusScoped>
            {({ statusView, setStatusView }) => {
              const metrics = getInvoiceMetricsForStatusView(statusView);
              const postcodeData = getRevenueByPostcodeChartData(statusView);
              return (
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography level="title-lg">
                        {metrics.selectedLabel} Revenue by Postcode Area
                      </Typography>
                      <InvoiceStatusToggle value={statusView} onChange={setStatusView} />
                    </Box>
                    {Object.keys(revenueByPostcodeByStatus[statusView] || {}).length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={postcodeData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"

                          >
                            {postcodeData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value: any) => [formatCurrency(value), 'Revenue']} 
                            labelFormatter={(label) => `${label} Revenue`}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: 300,
                        color: 'text.secondary'
                      }}>
                        <Typography level="body-md">
                          No postcode data available
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            }}
          </InvoiceStatusScoped>
        </Grid>

        {/* Projects by Postcode Area */}
        <Grid xs={12} lg={6} sx={{ order: 12 }}>
          <InvoiceStatusScoped>
            {({ statusView, setStatusView }) => {
              const metrics = getInvoiceMetricsForStatusView(statusView);
              const chartData = getProjectsByPostcodeChartData(statusView);
              return (
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography level="title-lg">
                        {metrics.selectedLabel} Invoices by Postcode Area
                      </Typography>
                      <InvoiceStatusToggle value={statusView} onChange={setStatusView} />
                    </Box>
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="postcode" />
                          <YAxis allowDecimals={false} />
                          <Tooltip 
                            formatter={(value: any, name: any) => [value, name]}
                            labelFormatter={(label) => `Postcode: ${label}`}
                          />
                          <Legend />
                          <Bar 
                            dataKey="complete" 
                            name={`${metrics.selectedLabel} Invoices`} 
                            fill="#82ca9d"

                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: 300,
                        color: 'text.secondary'
                      }}>
                        <Typography level="body-md">
                          No project data available
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            }}
          </InvoiceStatusScoped>
        </Grid>

        <Grid xs={12} sx={{ order: 4 }}>
          <Typography level="h4">Projects Analytics</Typography>
        </Grid>
        <Grid xs={12} sx={{ order: 9 }}>
          <Typography level="h4">Customer Demographics & Geography</Typography>
        </Grid>
      </Grid>

      <Typography level="h3" sx={{ mb: 2 }}>
        Fenestration
      </Typography>
      <Typography level="title-md" sx={{ mb: 1.5, color: 'text.secondary' }}>
        Supplier
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <JoyTooltip
                title="Estimated Type totals for active quotes/estimates created within the selected period."
                arrow
              >
                <Typography level="title-lg" sx={{ mb: 2, cursor: 'help', width: 'fit-content' }}>
                  Estimated Additional Costs by Type
                </Typography>
              </JoyTooltip>
              {estimatedAdditionalCostTypeData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={estimatedAdditionalCostTypeData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value: number) => `£${Number(value || 0).toFixed(0)}`} />
                      <Tooltip
                        formatter={(value: any) => [formatCurrency(Number(value || 0)), 'Additional Cost']}
                        labelFormatter={(label: any) => `${label}`}
                      />
                      <Bar
                        dataKey="value"
                        onClick={(data: any) => {
                          const key = String(data?.key || data?.payload?.key || '').trim();
                          if (!key) return;
                          navigate(getAdditionalCostsDrilldownPath('estimated', key));
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {estimatedAdditionalCostTypeData.map((entry, index) => (
                          <Cell key={`additional-type-cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <Box
                    sx={{
                      mt: 1,
                      overflowX: 'auto',
                      '& thead th': { bgcolor: 'background.level1', fontWeight: 'lg' },
                      '& tbody td, & tfoot td': { py: 0.75 },
                      '& a': { color: 'primary.600', textDecoration: 'none' },
                      '& a:hover': { textDecoration: 'underline' }
                    }}
                  >
                    <Table size="sm">
                      <thead>
                        <tr>
                          <th>Type 1 (Grouped)</th>
                          <th style={{ textAlign: 'right' }}>Total</th>
                          <th>Type 2 (Original)</th>
                          <th style={{ textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const groupedRows = getAdditionalTypeGroupedRows(estimatedAdditionalCostTypeData);
                          const originalRows = getAdditionalTypeOriginalRows(estimatedAdditionalCostTypeData);
                          const rowCount = Math.max(groupedRows.length, originalRows.length);
                          return Array.from({ length: rowCount }).map((_, idx) => {
                            const grouped = groupedRows[idx];
                            const original = originalRows[idx];
                            return (
                              <tr key={`estimated-breakdown-${idx}`}>
                                <td>
                                  {grouped ? (
                                    <Link to={getAdditionalCostsDrilldownPath('estimated', grouped.drilldownType)}>
                                      {grouped.label}
                                    </Link>
                                  ) : '—'}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {grouped ? formatCurrency(grouped.value) : '—'}
                                </td>
                                <td>
                                  {original ? (
                                    <Link to={getAdditionalCostsDrilldownPath('estimated', original.drilldownType)}>
                                      {original.label}
                                    </Link>
                                  ) : '—'}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {original ? formatCurrency(original.value) : '—'}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td><strong>Grand Total</strong></td>
                          <td style={{ textAlign: 'right' }}><strong>{formatCurrency(estimatedAdditionalGrandTotal)}</strong></td>
                          <td><strong>Grand Total</strong></td>
                          <td style={{ textAlign: 'right' }}><strong>{formatCurrency(estimatedAdditionalGrandTotal)}</strong></td>
                        </tr>
                      </tfoot>
                    </Table>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300,
                    color: 'text.secondary'
                  }}
                >
                  <Typography level="body-md">
                    No additional cost type data in selected period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <JoyTooltip
                title="Type totals for Accepted quotes/estimates where accepted date falls within the selected period."
                arrow
              >
                <Typography level="title-lg" sx={{ mb: 2, cursor: 'help', width: 'fit-content' }}>
                  Accepted Quote/Estimate Additional Costs by Type
                </Typography>
              </JoyTooltip>
              {acceptedAdditionalCostTypeData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={acceptedAdditionalCostTypeData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value: number) => `£${Number(value || 0).toFixed(0)}`} />
                      <Tooltip
                        formatter={(value: any) => [formatCurrency(Number(value || 0)), 'Accepted Additional Cost']}
                        labelFormatter={(label: any) => `${label}`}
                      />
                      <Bar
                        dataKey="value"
                        onClick={(data: any) => {
                          const key = String(data?.key || data?.payload?.key || '').trim();
                          if (!key) return;
                          navigate(getAdditionalCostsDrilldownPath('accepted', key));
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {acceptedAdditionalCostTypeData.map((entry, index) => (
                          <Cell key={`accepted-additional-type-cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <Box
                    sx={{
                      mt: 1,
                      overflowX: 'auto',
                      '& thead th': { bgcolor: 'background.level1', fontWeight: 'lg' },
                      '& tbody td, & tfoot td': { py: 0.75 },
                      '& a': { color: 'primary.600', textDecoration: 'none' },
                      '& a:hover': { textDecoration: 'underline' }
                    }}
                  >
                    <Table size="sm">
                      <thead>
                        <tr>
                          <th>Type 1 (Grouped)</th>
                          <th style={{ textAlign: 'right' }}>Total</th>
                          <th>Type 2 (Original)</th>
                          <th style={{ textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const groupedRows = getAdditionalTypeGroupedRows(acceptedAdditionalCostTypeData);
                          const originalRows = getAdditionalTypeOriginalRows(acceptedAdditionalCostTypeData);
                          const rowCount = Math.max(groupedRows.length, originalRows.length);
                          return Array.from({ length: rowCount }).map((_, idx) => {
                            const grouped = groupedRows[idx];
                            const original = originalRows[idx];
                            return (
                              <tr key={`accepted-breakdown-${idx}`}>
                                <td>
                                  {grouped ? (
                                    <Link to={getAdditionalCostsDrilldownPath('accepted', grouped.drilldownType)}>
                                      {grouped.label}
                                    </Link>
                                  ) : '—'}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {grouped ? formatCurrency(grouped.value) : '—'}
                                </td>
                                <td>
                                  {original ? (
                                    <Link to={getAdditionalCostsDrilldownPath('accepted', original.drilldownType)}>
                                      {original.label}
                                    </Link>
                                  ) : '—'}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {original ? formatCurrency(original.value) : '—'}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td><strong>Grand Total</strong></td>
                          <td style={{ textAlign: 'right' }}><strong>{formatCurrency(acceptedAdditionalGrandTotal)}</strong></td>
                          <td><strong>Grand Total</strong></td>
                          <td style={{ textAlign: 'right' }}><strong>{formatCurrency(acceptedAdditionalGrandTotal)}</strong></td>
                        </tr>
                      </tfoot>
                    </Table>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300,
                    color: 'text.secondary'
                  }}
                >
                  <Typography level="body-md">
                    No accepted quote/estimate additional item data in selected period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Internal Finish"
            subtitle={renderFenestrationSubtitle(fenestrationSummary.internalFinishCounts, 'No other finishes')}
            icon={FaBoxes}
            color="primary"
            flatDetails
            onClick={() => navigate(getFenestrationDrilldownPath('internal_finish'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="External Finish"
            subtitle={renderFenestrationSubtitle(fenestrationSummary.externalFinishCounts, 'No other finishes')}
            icon={FaBoxes}
            color="primary"
            flatDetails
            onClick={() => navigate(getFenestrationDrilldownPath('external_finish'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Material Type"
            subtitle={renderFenestrationSubtitle(fenestrationSummary.materialTypeCounts, 'No other materials')}
            icon={FaBoxes}
            color="primary"
            flatDetails
            onClick={() => navigate(getFenestrationDrilldownPath('material_type'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Type"
            subtitle={renderFenestrationSubtitle(fenestrationSummary.typeCounts, 'No other types')}
            icon={FaClipboardList}
            color="primary"
            flatDetails
            onClick={() => navigate(getFenestrationDrilldownPath('type'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Glass Type"
            subtitle={renderFenestrationSubtitle(fenestrationSummary.glassTypeCounts, 'No other glass types')}
            icon={FaBoxes}
            color="primary"
            flatDetails
            onClick={() => navigate(getFenestrationDrilldownPath('glass_type'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Equal Width/Height"
            value={formatNumber(fenestrationSummary.sameDimensionOccurrences)}
            subtitle="Occurrences where width matches height"
            icon={FaChartBar}
            color="warning"
            onClick={() => navigate(getFenestrationDrilldownPath('equal_dims'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Equal Width/Height Within 50mm"
            value={formatNumber(fenestrationSummary.withinFiftyMmDimensionOccurrences)}
            subtitle="Occurrences where width and height differ by <= 50mm"
            icon={FaChartBar}
            color="warning"
            onClick={() => navigate(getFenestrationDrilldownPath('within_50_dims'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Mean Window Dimensions"
            value={formatMeanDimensions(fenestrationSummary.meanWindowWidth, fenestrationSummary.meanWindowHeight)}
            subtitle="Weighted by item occurrences"
            icon={FaChartArea}
            color="success"
            onClick={() => navigate(getFenestrationDrilldownPath('mean_window'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Mean Door Dimensions"
            value={formatMeanDimensions(fenestrationSummary.meanDoorWidth, fenestrationSummary.meanDoorHeight)}
            subtitle="Weighted by item occurrences"
            icon={FaChartArea}
            color="success"
            onClick={() => navigate(getFenestrationDrilldownPath('mean_door'))}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <StatCard
            title="Openers"
            value={formatNumber(fenestrationSummary.totalOpeners)}
            subtitle={`${formatNumber(fenestrationSummary.itemsWithOpeners)} items with openers`}
            icon={FaChartLine}
            color="primary"
            onClick={() => navigate(getFenestrationDrilldownPath('openers'))}
          />
        </Grid>
      </Grid>

      <Modal open={vatWorkingsOpen} onClose={() => setVatWorkingsOpen(false)}>
        <ModalDialog sx={{ minWidth: 900, maxWidth: '95vw' }}>
          <DialogTitle>Net VAT Workings (Paid Invoices)</DialogTitle>
          <DialogContent>
            <Stack spacing={1.25}>
              <Table size="sm" hoverRow>
                <thead>
                  <tr>
                    <th>Invoice Ref</th>
                    <th>Customer</th>
                    <th style={{ textAlign: 'right' }}>Input VAT Total</th>
                    <th style={{ textAlign: 'right' }}>Output VAT Total</th>
                    <th style={{ textAlign: 'right' }}>Net VAT Position</th>
                  </tr>
                </thead>
                <tbody>
                  {vatWorkingsRows.length === 0 ? (
                    <tr>
                      <td colSpan={5}>No paid invoices in the selected period.</td>
                    </tr>
                  ) : (
                    vatWorkingsRows.map((row) => (
                      <tr key={row.documentId}>
                        <td>
                          <Link to={`/docs/${row.documentId}`}>{row.ref}</Link>
                        </td>
                        <td>{row.customerName}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(row.inputVat)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(row.outputVat)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(row.netOff)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              <Divider />
              <Stack direction="row" spacing={3}>
                <Typography level="body-sm"><strong>Input VAT Total:</strong> {formatCurrency(vatSummary.inputVat)}</Typography>
                <Typography level="body-sm"><strong>Output VAT Total:</strong> {formatCurrency(vatSummary.outputVat)}</Typography>
                <Typography level="body-sm"><strong>Net VAT Position:</strong> {formatCurrency(vatSummary.netVatPosition)}</Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Positive = owed to HMRC; Negative = VAT refund due
                </Typography>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setVatWorkingsOpen(false)}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default DashboardPage; 